import { useState, useCallback, useRef, useEffect } from 'react';
import type { ListingFilter } from '@/features/plp/domain/models/ProductListing';
import type { FiltersService } from '@/features/filters/application/services/FiltersService';
import { PLP_PAGE_TYPE } from '@/features/plp/constants';
import { getPlpFilterPreviewTags } from '@/features/filters/utils/buildPlpFilterPreviewTags';

type SelectedOptions = Record<string, string[]>;

function deriveSelectedOptions(filters: ListingFilter[]): SelectedOptions {
  const result: SelectedOptions = {};
  for (const f of filters) {
    const selected = f.items.filter((i) => i.selected).map((i) => i.name);
    if (selected.length > 0) result[f.category] = selected;
  }
  return result;
}

/** Match PLP desktop: preview debounce, then 2s idle before commit. */
const PREVIEW_DEBOUNCE_MS = 400;
const COMMIT_DEBOUNCE_MS = 2000;

export interface UseRecommendedBrowseFacetFiltersParams {
  listingFilters: ListingFilter[];
  filtersResetUrl?: string;
  baseTags: string;
  isDesktop: boolean;
  /** Skip syncing sidebar state from API while the grid is loading. */
  isListingLoading?: boolean;
  service: FiltersService;
  /** Mobile: Apply. Desktop: fired 2s after last toggle (same timing as PLP desktop). */
  onCommit: (applyUrl: string | null) => void;
  /** Called immediately before `onCommit` when the user applies facet filters (not on reset). */
  onApplyCommitted?: (ctx: { previewFilters: ListingFilter[]; selectedOptions: SelectedOptions }) => void;
}

export interface UseRecommendedBrowseFacetFiltersReturn {
  isOpen: boolean;
  openFilterSheet: () => void;
  /** Open if closed, close if open (desktop popover + mobile sheet). */
  toggleFilterSheet: () => void;
  previewFilters: ListingFilter[];
  selectedOptions: SelectedOptions;
  applyUrl: string | null;
  isLoadingPreview: boolean;
  /** Desktop: true while waiting for the 2s commit timer. */
  isPendingCommit: boolean;
  toggleOption: (category: string, name: string, url: string) => void;
  handleApply: () => void;
  handleReset: () => void;
  handleClose: () => void;
}

/**
 * Recommended section facet filters: PLP preview API + either explicit Apply (mobile)
 * or auto-commit after 2s idle on desktop (same pattern as usePlpDesktopFilters).
 */
export function useRecommendedBrowseFacetFilters({
  listingFilters,
  filtersResetUrl,
  baseTags,
  isDesktop,
  isListingLoading = false,
  service,
  onCommit,
  onApplyCommitted,
}: UseRecommendedBrowseFacetFiltersParams): UseRecommendedBrowseFacetFiltersReturn {
  const [isOpen, setIsOpen] = useState(false);
  const [previewFilters, setPreviewFilters] = useState<ListingFilter[]>(listingFilters);
  const [selectedOptions, setSelectedOptions] = useState<SelectedOptions>({});
  const [applyUrl, setApplyUrl] = useState<string | null>(null);
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);
  const [isPendingCommit, setIsPendingCommit] = useState(false);

  const previewDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const commitTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const selectedOptionsRef = useRef<SelectedOptions>({});
  const baseFilterUrlRef = useRef<string | null>(null);
  const filtersResetUrlRef = useRef(filtersResetUrl);
  filtersResetUrlRef.current = filtersResetUrl;
  const onCommitRef = useRef(onCommit);
  onCommitRef.current = onCommit;
  const onApplyCommittedRef = useRef(onApplyCommitted);
  onApplyCommittedRef.current = onApplyCommitted;

  const previewFiltersRef = useRef(previewFilters);
  previewFiltersRef.current = previewFilters;

  useEffect(() => {
    return () => {
      if (previewDebounceRef.current) clearTimeout(previewDebounceRef.current);
      if (commitTimerRef.current) clearTimeout(commitTimerRef.current);
    };
  }, []);

  useEffect(() => {
    if (isListingLoading || commitTimerRef.current || isOpen) return;
    setPreviewFilters(listingFilters);
    const synced = deriveSelectedOptions(listingFilters);
    setSelectedOptions(synced);
    selectedOptionsRef.current = synced;
    baseFilterUrlRef.current = null;
    setApplyUrl(null);
  }, [listingFilters, isListingLoading, isOpen]);

  const schedulePreviewUpdate = useCallback(
    (nextSelected: SelectedOptions, filterItemUrl?: string | null) => {
      if (previewDebounceRef.current) clearTimeout(previewDebounceRef.current);
      previewDebounceRef.current = setTimeout(() => {
        const previewTags = getPlpFilterPreviewTags(filterItemUrl, baseTags);
        setIsLoadingPreview(true);
        service
          .getFilters({
            page_type: PLP_PAGE_TYPE.HOME,
            tags: previewTags,
            tags_to_sort_filters: baseTags,
            is_for_filter_change: true,
          })
          .then((result) => {
            if (result.filters.length > 0) {
              setPreviewFilters(result.filters);
            }
            if (result.filtersResetUrl) {
              filtersResetUrlRef.current = result.filtersResetUrl;
            }
            const allSelectedNames = Object.values(nextSelected).flat();
            if (allSelectedNames.length === 0) {
              setApplyUrl(filtersResetUrlRef.current ?? null);
            }
          })
          .catch(() => {})
          .finally(() => {
            setIsLoadingPreview(false);
          });
      }, PREVIEW_DEBOUNCE_MS);
    },
    [service, baseTags]
  );

  const handleClose = useCallback(() => {
    if (previewDebounceRef.current) {
      clearTimeout(previewDebounceRef.current);
      previewDebounceRef.current = null;
    }
    if (commitTimerRef.current) {
      clearTimeout(commitTimerRef.current);
      commitTimerRef.current = null;
    }
    setIsPendingCommit(false);
    setIsOpen(false);
    setPreviewFilters(listingFilters);
    const synced = deriveSelectedOptions(listingFilters);
    setSelectedOptions(synced);
    selectedOptionsRef.current = synced;
    baseFilterUrlRef.current = null;
    setApplyUrl(null);
    setIsLoadingPreview(false);
  }, [listingFilters]);

  const scheduleCommit = useCallback(() => {
    if (!isDesktop) return;
    if (commitTimerRef.current) clearTimeout(commitTimerRef.current);
    setIsPendingCommit(true);
    commitTimerRef.current = setTimeout(() => {
      commitTimerRef.current = null;
      setIsPendingCommit(false);
      const url = baseFilterUrlRef.current;
      baseFilterUrlRef.current = null;
      onApplyCommittedRef.current?.({
        previewFilters: previewFiltersRef.current,
        selectedOptions: selectedOptionsRef.current,
      });
      if (!url) {
        if (filtersResetUrlRef.current) onCommitRef.current(filtersResetUrlRef.current);
        else onCommitRef.current(null);
      } else {
        onCommitRef.current(url);
      }
      handleClose();
    }, COMMIT_DEBOUNCE_MS);
  }, [isDesktop, handleClose]);

  const openFilterSheet = useCallback(() => {
    if (commitTimerRef.current) {
      clearTimeout(commitTimerRef.current);
      commitTimerRef.current = null;
    }
    setIsPendingCommit(false);
    setPreviewFilters(listingFilters);
    const initial = deriveSelectedOptions(listingFilters);
    setSelectedOptions(initial);
    selectedOptionsRef.current = initial;
    baseFilterUrlRef.current = null;
    setApplyUrl(null);
    setIsOpen(true);
  }, [listingFilters]);

  const toggleFilterSheet = useCallback(() => {
    if (isOpen) {
      handleClose();
    } else {
      openFilterSheet();
    }
  }, [isOpen, handleClose, openFilterSheet]);

  const toggleOption = useCallback(
    (category: string, name: string, url: string) => {
      const current = selectedOptions[category] ?? [];
      const isSelected = current.includes(name);
      let next: SelectedOptions;
      if (isSelected) {
        const filtered = current.filter((n) => n !== name);
        if (filtered.length === 0) {
          const { [category]: _removed, ...rest } = selectedOptions;
          next = rest;
        } else {
          next = { ...selectedOptions, [category]: filtered };
        }
      } else {
        next = { ...selectedOptions, [category]: [...current, name] };
      }
      setSelectedOptions(next);
      selectedOptionsRef.current = next;
      if (url) {
        baseFilterUrlRef.current = url;
        setApplyUrl(url);
      }
      schedulePreviewUpdate(next, url);
      if (isDesktop) {
        scheduleCommit();
      }
    },
    [selectedOptions, schedulePreviewUpdate, scheduleCommit, isDesktop]
  );

  const handleApply = useCallback(() => {
    if (isDesktop) return;
    const url = applyUrl ?? filtersResetUrlRef.current ?? null;
    onApplyCommittedRef.current?.({
      previewFilters: previewFiltersRef.current,
      selectedOptions: selectedOptionsRef.current,
    });
    onCommitRef.current(url);
    setIsOpen(false);
  }, [isDesktop, applyUrl]);

  const handleReset = useCallback(() => {
    if (previewDebounceRef.current) {
      clearTimeout(previewDebounceRef.current);
      previewDebounceRef.current = null;
    }
    if (commitTimerRef.current) {
      clearTimeout(commitTimerRef.current);
      commitTimerRef.current = null;
    }
    setIsPendingCommit(false);

    const cleared: SelectedOptions = {};
    setSelectedOptions(cleared);
    selectedOptionsRef.current = cleared;
    baseFilterUrlRef.current = null;
    setPreviewFilters(listingFilters);
    setApplyUrl(null);

    /** Reset = show All: clear `filters` query (not PLP category-only reset URL). */
    onCommitRef.current(null);
    handleClose();
  }, [listingFilters, handleClose]);

  return {
    isOpen,
    openFilterSheet,
    toggleFilterSheet,
    previewFilters,
    selectedOptions,
    applyUrl,
    isLoadingPreview,
    isPendingCommit,
    toggleOption,
    handleApply,
    handleReset,
    handleClose,
  };
}
